/**
 * Utility functions for fuzzy location name matching
 */

/**
 * Normalizes location names for comparison by removing special characters,
 * whitespace, and converting to lowercase
 */
export const normalizeLocationName = (location: string): string => {
  return location
    .toLowerCase()
    .replace(/[(),\s\-\/]+/g, "")
    .replace(/multioutletc/gi, "")
    .trim();
};

/**
 * Extracts known city names from compound location strings
 * e.g., "buffalorochester" -> ["buffalo", "rochester"]
 */
export const extractCities = (str: string): string[] => {
  const cities = [
    'baltimore', 'washington',
    'buffalo', 'rochester',
    'phoenix', 'tucson'
  ];
  return cities.filter(city => str.includes(city));
};

/**
 * Performs fuzzy matching between two location names
 * Returns true if locations match based on:
 * 1. Direct match after normalization
 * 2. Compound city name matching (e.g., "baltimorewashington")
 * 3. Word overlap matching
 */
export const fuzzyMatchLocation = (
  targetLocation: string,
  candidateLocation: string
): boolean => {
  const normalizedTarget = normalizeLocationName(targetLocation);
  const normalizedCandidate = normalizeLocationName(candidateLocation);

  // Direct match after normalization
  if (normalizedTarget === normalizedCandidate) {
    return true;
  }

  // Extract city names from compound names
  const targetCities = extractCities(normalizedTarget);
  const candidateCities = extractCities(normalizedCandidate);

  // If we found cities in the target name, check if they all appear in the candidate
  if (targetCities.length > 0) {
    const allMatch = targetCities.every(city =>
      normalizedCandidate.includes(city)
    );
    if (allMatch) {
      return true;
    }
  }

  // Fuzzy match: check if words overlap
  const targetWords = normalizedTarget.split(/[,\s-]+/).filter(Boolean);
  const candidateWords = normalizedCandidate.split(/[,\s-]+/).filter(Boolean);

  const matchingWords = targetWords.filter(word =>
    candidateWords.some(candidateWord =>
      candidateWord.includes(word) || word.includes(candidateWord)
    )
  );

  return matchingWords.length >= 1;
};

/**
 * Finds a matching location from a list of candidates
 */
export const findMatchingLocation = <T extends { locationOriginal?: string }>(
  targetLocation: string,
  candidates: T[]
): T | undefined => {
  return candidates.find((candidate) => {
    if (!candidate.locationOriginal) return false;
    return fuzzyMatchLocation(targetLocation, candidate.locationOriginal);
  });
};
